import { useQuery } from "react-query";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { SortType } from "src/api/apiHelpers";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { SupplierService } from "src/pages/Supplier/SupplierService";
import { isQueryReady } from "src/api/axiosClient/utils";
import { hydraSearchToAppSearch } from "../../_common/hydra/search";

export const allOrganizationKey = "getAllCompany" as const;

type Options = {
    cacheTime?: number;
    page?: number;
    itemsPerPage?: number;
    search?: string;
    isProvider?: boolean;
    hierarchyRootId?: number;
    organizationType?: OrganizationType[] | OrganizationType;
    ingredientLine?: string;
    sorting?: Array<SortType>;
    type?: string[];
    additionalParams?: Record<string, any>;
    enabled?: boolean;
};

export const fetchOrganizationGrid = async (url: string, params: Record<string, unknown>, logKey: string) => {
    const request = await loggedInApiCall.get(url, { params });
    const organizationsWithSectors = SupplierService.makeCompanyListWithSectors(request.data["hydra:member"]);
    const hydraSearch = request.data["hydra:search"];

    return {
        organizations: zodParseWithLog(organizationsWithSectors, CompanyGridZodSchema.array(), logKey),
        totalItems: request.data["hydra:totalItems"],
        appSearch: hydraSearch === undefined ? {} : hydraSearchToAppSearch(hydraSearch),
    };
};

export type CompanyType = "provider"; // Only for suppliers now
export type OrganizationType = {
    id: number;
    name: string;
};

export const useGetAllOrganization = (options?: Options) => {
    const {
        cacheTime = 0,
        page = 1,
        itemsPerPage = 20,
        search = "",
        isProvider,
        hierarchyRootId,
        organizationType = [],
        ingredientLine = "",
        sorting = [],
        type,
        additionalParams = {},
        enabled = true,
    } = options || {};

    const query = useQuery({
        queryKey: [
            allOrganizationKey,
            page,
            itemsPerPage,
            search,
            isProvider,
            hierarchyRootId,
            ingredientLine,
            sorting,
            type,
            JSON.stringify(additionalParams),
        ],
        enabled,
        staleTime: 0,
        cacheTime: cacheTime,
        queryFn: async () => {
            const url = `/api/organizations`;

            const params = {
                itemsPerPage: itemsPerPage,
                page: page,
                q: search !== "" ? search : undefined,
                // "ingredientSuppliers.ingredient.ingredientLine.name":
                //     ingredientLine !== "" ? ingredientLine : undefined,
                isProvider:
                    isProvider !== undefined ?
                        isProvider ? 1
                        :   0
                    :   undefined,
                hierarchyRootId,
                organizationType: organizationType,
                type: type,
                // order: mrtSortingStateToObject(sorting),
                ...additionalParams,
            };

            const { organizations, totalItems, appSearch } = await fetchOrganizationGrid(
                url,
                params,
                allOrganizationKey,
            );
            return {
                companies: organizations,
                totalItems: totalItems,
                appSearch,
            };
        },
        onError: () => {
            return {
                companies: [],
                totalItems: 0,
                appSearch: {},
            };
        },
    });

    const isReady = isQueryReady(query, true);

    return {
        ...query.data,
        isReady,
        ...query,
    };
};

export type IngredientLine = {
    "@id": IRI;
    id: number;
    name: string;
};

export const IngredientLineZodSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type CompanySector = {
    "@id": string;
    id: number;
    name: string;
};

export const CompanySectorZod: z.ZodSchema<CompanySector> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type UserSupplier = {
    firstname: string;
    lastname: string;
    picture?: string;
};

export const UserSupplierZod: z.ZodSchema<UserSupplier> = z.object({
    firstname: z.string(),
    lastname: z.string(),
    picture: z.string().optional(),
});

export type NewIngredientFromCompanyGrid = {
    "@id"?: IRI;
    id?: number;
    name: string;
    ingredientLine?: IngredientLine;
};

export type IngredientCompanyGrid = {
    "@id"?: IRI;
    ingredient: NewIngredientFromCompanyGrid;
};

export type Domain = {
    "@id": IRI;
    name: string;
};

export const DomainSchema = z.object({
    "@id": z.string(),
    name: z.string(),
});

export type CompanyGrid = {
    "@id": IRI;
    id: number;
    name: string;
    clientId?: string;
    registrationNumber?: string;
    externalId?: string;
    domains: Domain[];
    organizationSector?: CompanySector;
    ingredientSuppliers?: IngredientCompanyGrid[];
    sectors: string[];
    address1?: string;
    address2?: string;
    city?: string;
    country?: string;
    zipcode?: string;
    state?: string;
    phone?: string;
    email?: string;
    isProvider?: boolean; // Boolean to indicate if it's a supplier
    isPlatformOwner?: boolean;
    organizationType?: {
        id: number;
        name: string;
    } | null; // For organizational structure
    users?: UserSupplier[];
    updatedAt?: string;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    tags?: Record<string, string[]>; // Format object groupé par slug
    jsonAttributes?: Record<string, any>;
    parents?: Array<{
        "@id": string;
        id: number;
        name: string;
    }>; // Parents companies (level +1 only)
};

export const CompanyGridZodSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    clientId: z.string().optional(),
    registrationNumber: z.string().optional(),
    externalId: z.string().optional(),
    domains: DomainSchema.array(),
    organizationSector: CompanySectorZod.optional(),
    isProvider: z.boolean().optional(), // Boolean to indicate if it's a supplier
    isPlatformOwner: z.boolean().optional(),
    organizationType: z
        .object({
            id: z.number(),
            name: z.string(),
        })
        .optional()
        .nullable(), // For organizational structure
    ingredientSuppliers: z
        .object({
            "@id": z.string().optional(),
            ingredient: z.object({
                id: z.number(),
                name: z.string(),
                ingredientLine: z
                    .object({
                        "@id": z.string(),
                        id: z.number(),
                        name: z.string(),
                    })
                    .optional(),
            }),
        })
        .array()
        .optional(),
    sectors: z.string().array(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    zipcode: z.string().optional(),
    state: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    users: UserSupplierZod.array().optional(),
    updatedAt: z.string().optional(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    tags: z
        .union([
            z.record(z.string(), z.array(z.string())), // Format objet standard
            z.array(z.never()).length(0), // Tableau vide uniquement
        ])
        .optional()
        .transform((value): Record<string, string[]> | undefined => {
            // Convertir tableau vide en objet vide pour cohérence
            return (
                value === undefined ? undefined
                : Array.isArray(value) ? {}
                : value
            );
        }) as unknown as z.ZodOptional<z.ZodType<Record<string, string[]>>>,
    jsonAttributes: z
        .union([z.record(z.string(), z.any()), z.array(z.any())])
        .optional()
        .transform((val) => {
            // Si c'est un tableau, le convertir en objet vide pour compatibilité
            if (Array.isArray(val)) {
                return {};
            }
            return val;
        }),
    parents: z
        .array(
            z.object({
                "@id": z.string(),
                id: z.number(),
                name: z.string(),
            }),
        )
        .optional(),
});

// Schéma pour accepter soit un objet soit un tableau vide (API Platform)
const CompanyTagsSchema = z
    .union([
        z.record(z.string(), z.array(z.string())), // Format objet standard
        z.array(z.never()).length(0), // Tableau vide uniquement
    ])
    .transform((value): Record<string, string[]> => {
        // Convertir tableau vide en objet vide pour cohérence
        return Array.isArray(value) ? {} : value;
    }) as z.ZodType<Record<string, string[]>>;
